import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  FileText, Upload, Eye, Save, Check, Palette,
  Building2, Phone, Mail, Globe, MapPin, Loader2, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATE_PREVIEWS = {
  classic: {
    name: 'Classic',
    desc: 'Logo left, info right, horizontal line',
    render: (colors) => (
      <div className="w-full h-full flex flex-col text-[6px]">
        <div className="flex justify-between items-start p-2">
          <div className="w-10 h-5 rounded" style={{ backgroundColor: colors.primary + '22' }}>
            <div className="w-6 h-3 m-1 rounded-sm" style={{ backgroundColor: colors.primary }} />
          </div>
          <div className="text-right space-y-0.5">
            <div className="font-bold" style={{ color: colors.primary }}>Company Name</div>
            <div className="text-gray-400">123 Address St</div>
            <div className="text-gray-400">Phone | Email</div>
          </div>
        </div>
        <div className="mx-2 h-[1.5px]" style={{ backgroundColor: colors.primary }} />
        <div className="flex-1 p-2 space-y-1">
          <div className="w-16 h-1 bg-gray-200 rounded" />
          <div className="w-24 h-1 bg-gray-200 rounded" />
          <div className="w-full h-1 bg-gray-100 rounded mt-2" />
          <div className="w-full h-1 bg-gray-100 rounded" />
          <div className="w-3/4 h-1 bg-gray-100 rounded" />
        </div>
        <div className="border-t border-gray-200 px-2 py-1 flex justify-between text-gray-400">
          <span>Company</span><span>Address</span><span>Web</span>
        </div>
      </div>
    ),
  },
  modern: {
    name: 'Modern',
    desc: 'Bold colored header bar, contemporary',
    render: (colors) => (
      <div className="w-full h-full flex flex-col text-[6px]">
        <div className="p-2 text-white flex items-center gap-2" style={{ backgroundColor: colors.primary }}>
          <div className="w-8 h-4 bg-white/30 rounded-sm" />
          <span className="font-bold text-[7px]">COMPANY NAME</span>
        </div>
        <div className="h-[2px]" style={{ backgroundColor: colors.secondary }} />
        <div className="text-center py-1 text-gray-400">Address | Phone | Email</div>
        <div className="flex-1 p-2 space-y-1">
          <div className="w-16 h-1 bg-gray-200 rounded" />
          <div className="w-24 h-1 bg-gray-200 rounded" />
          <div className="w-full h-1 bg-gray-100 rounded mt-2" />
          <div className="w-full h-1 bg-gray-100 rounded" />
          <div className="w-3/4 h-1 bg-gray-100 rounded" />
        </div>
        <div className="text-white text-center py-1" style={{ backgroundColor: colors.primary }}>
          <div className="h-[1.5px] mb-0.5" style={{ backgroundColor: colors.secondary }} />
          Footer Info
        </div>
      </div>
    ),
  },
  minimal: {
    name: 'Minimal',
    desc: 'Clean & simple, subtle branding',
    render: (colors) => (
      <div className="w-full h-full flex flex-col text-[6px]">
        <div className="flex items-center gap-1.5 p-2">
          <div className="w-7 h-4 rounded-sm" style={{ backgroundColor: colors.primary + '22' }}>
            <div className="w-4 h-2 m-1 rounded-sm" style={{ backgroundColor: colors.primary }} />
          </div>
          <span className="font-bold" style={{ color: colors.primary }}>Company</span>
        </div>
        <div className="ml-2 w-8 h-[1px]" style={{ backgroundColor: colors.primary }} />
        <div className="flex-1 p-2 space-y-1 mt-1">
          <div className="w-14 h-1 bg-gray-200 rounded" />
          <div className="w-20 h-1 bg-gray-200 rounded" />
          <div className="w-full h-1 bg-gray-100 rounded mt-2" />
          <div className="w-full h-1 bg-gray-100 rounded" />
          <div className="w-3/4 h-1 bg-gray-100 rounded" />
        </div>
        <div className="text-center py-1 text-gray-400">Company | Phone | Email</div>
      </div>
    ),
  },
  corporate: {
    name: 'Corporate',
    desc: 'Bold sidebar stripe, structured layout',
    render: (colors) => (
      <div className="w-full h-full flex flex-col text-[6px] relative">
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ backgroundColor: colors.primary }} />
        <div className="bg-gray-50 flex items-center justify-between p-2 ml-[3px]">
          <div className="w-10 h-5 rounded-sm" style={{ backgroundColor: colors.primary + '22' }}>
            <div className="w-6 h-3 m-1 rounded-sm" style={{ backgroundColor: colors.primary }} />
          </div>
          <div className="text-right">
            <div className="font-bold" style={{ color: colors.primary }}>Company</div>
            <div className="text-gray-400">Address | Phone</div>
          </div>
        </div>
        <div className="h-[1.5px] ml-[3px]" style={{ backgroundColor: colors.secondary }} />
        <div className="flex-1 p-2 ml-[3px] space-y-1">
          <div className="w-14 h-1 bg-gray-200 rounded" />
          <div className="w-full h-1 bg-gray-100 rounded mt-2" />
          <div className="w-full h-1 bg-gray-100 rounded" />
          <div className="w-3/4 h-1 bg-gray-100 rounded" />
        </div>
        <div className="ml-[3px] text-white text-center py-1" style={{ backgroundColor: colors.primary }}>
          Footer Info
        </div>
      </div>
    ),
  },
  elegant: {
    name: 'Elegant',
    desc: 'Thin border frame, premium feel',
    render: (colors) => (
      <div className="w-full h-full p-1.5">
        <div className="w-full h-full border rounded-sm flex flex-col text-[6px]" style={{ borderColor: colors.primary }}>
          <div className="text-center p-1.5 space-y-0.5">
            <div className="w-8 h-4 mx-auto rounded-sm" style={{ backgroundColor: colors.primary + '22' }}>
              <div className="w-5 h-2 mx-auto pt-1 rounded-sm" style={{ backgroundColor: colors.primary }} />
            </div>
            <div className="font-bold" style={{ color: colors.primary }}>Company Name</div>
            <div className="text-gray-400">Address Line</div>
            <div className="text-gray-400">Phone | Email</div>
            <div className="w-8 mx-auto h-[0.5px]" style={{ backgroundColor: colors.primary }} />
          </div>
          <div className="flex-1 px-2 space-y-1">
            <div className="w-14 h-1 bg-gray-200 rounded" />
            <div className="w-full h-1 bg-gray-100 rounded mt-1" />
            <div className="w-full h-1 bg-gray-100 rounded" />
            <div className="w-3/4 h-1 bg-gray-100 rounded" />
          </div>
          <div className="text-center py-1 text-gray-400">Website</div>
        </div>
      </div>
    ),
  },
};

const LetterheadSettings = () => {
  const { getAuthHeaders, getOfficeId } = useAuth();
  const officeId = getOfficeId();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [config, setConfig] = useState({
    template: 'classic',
    logo: '',
    company_name: '',
    address_line1: '',
    address_line2: '',
    phone: '',
    email: '',
    website: '',
    primary_color: '#0F2E6E',
    secondary_color: '#3498db',
    footer_text: '',
  });

  useEffect(() => {
    fetchLetterhead();
  }, []);

  const fetchLetterhead = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/offices/${officeId}/letterhead`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(prev => ({ ...prev, ...data.config }));
      }
    } catch (err) {
      console.error('Failed to load letterhead config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...config };
      // Don't send logo if it hasn't changed (to avoid massive payload)
      if (payload.logo && payload.logo.length > 1000 && !payload.logo.startsWith('data:')) {
        delete payload.logo;
      }
      const res = await fetch(`${API_URL}/api/offices/${officeId}/letterhead`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Letterhead configuration saved');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save letterhead configuration');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      // Save first to ensure the latest config is used
      const saveRes = await fetch(`${API_URL}/api/offices/${officeId}/letterhead`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!saveRes.ok) {
        toast.error('Failed to save before preview');
        return;
      }
      toast.success('Configuration saved');

      const res = await fetch(`${API_URL}/api/offices/${officeId}/letterhead/preview`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'letterhead_preview.pdf';
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        a.remove();
        toast.success('Preview PDF downloaded');
      } else {
        toast.error('Failed to generate preview');
      }
    } catch {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setConfig(prev => ({ ...prev, logo: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setConfig(prev => ({ ...prev, logo: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const colors = { primary: config.primary_color, secondary: config.secondary_color };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="letterhead-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Letterhead Templates</h1>
          <p className="text-sm text-gray-500 mt-1">Choose a design and customize it with your brand</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={previewing}
            data-testid="preview-letterhead-btn"
          >
            {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview PDF
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-letterhead-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Template Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Choose Template
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {Object.entries(TEMPLATE_PREVIEWS).map(([id, tpl]) => (
              <button
                key={id}
                onClick={() => updateField('template', id)}
                data-testid={`template-${id}`}
                className={`relative rounded-lg border-2 overflow-hidden transition-all hover:shadow-md group ${
                  config.template === id
                    ? 'border-blue-600 ring-2 ring-blue-200 shadow-md'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                {config.template === id && (
                  <div className="absolute top-1 right-1 z-10 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
                <div className="aspect-[3/4] bg-white dark:bg-gray-900">
                  {tpl.render(colors)}
                </div>
                <div className="p-1.5 bg-gray-50 dark:bg-gray-800 border-t">
                  <p className="text-xs font-medium text-center">{tpl.name}</p>
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo & Colors */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Logo & Brand Colors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Logo Upload */}
            <div>
              <Label className="text-sm font-medium">Office Logo</Label>
              <div className="mt-2 flex items-center gap-4">
                {config.logo ? (
                  <div className="relative w-28 h-16 border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                    <img
                      src={config.logo}
                      alt="Logo"
                      className="max-w-full max-h-full object-contain"
                      data-testid="logo-preview"
                    />
                    <button
                      onClick={removeLogo}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      data-testid="remove-logo-btn"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="w-28 h-16 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                    data-testid="upload-logo-area"
                  >
                    <Upload className="h-4 w-4 text-gray-400" />
                    <span className="text-[10px] text-gray-400 mt-0.5">Upload Logo</span>
                  </div>
                )}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-logo-btn"
                  >
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {config.logo ? 'Change' : 'Upload'}
                  </Button>
                  <p className="text-[10px] text-gray-400 mt-1">PNG or JPG, max 2MB</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Primary Color</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input
                    type="color"
                    value={config.primary_color}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    className="w-10 h-9 rounded cursor-pointer border"
                    data-testid="primary-color-picker"
                  />
                  <Input
                    value={config.primary_color}
                    onChange={(e) => updateField('primary_color', e.target.value)}
                    className="font-mono text-sm"
                    data-testid="primary-color-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Secondary Color</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input
                    type="color"
                    value={config.secondary_color}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    className="w-10 h-9 rounded cursor-pointer border"
                    data-testid="secondary-color-picker"
                  />
                  <Input
                    value={config.secondary_color}
                    onChange={(e) => updateField('secondary_color', e.target.value)}
                    className="font-mono text-sm"
                    data-testid="secondary-color-input"
                  />
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div className="flex gap-2">
              <div className="h-8 flex-1 rounded" style={{ backgroundColor: config.primary_color }} />
              <div className="h-8 flex-1 rounded" style={{ backgroundColor: config.secondary_color }} />
            </div>
          </CardContent>
        </Card>

        {/* Company Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Company Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Company Name</Label>
              <Input
                value={config.company_name}
                onChange={(e) => updateField('company_name', e.target.value)}
                placeholder="Your Company Ltd."
                className="mt-1"
                data-testid="company-name-input"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Address Line 1
                </Label>
                <Input
                  value={config.address_line1}
                  onChange={(e) => updateField('address_line1', e.target.value)}
                  placeholder="Street address"
                  className="mt-1"
                  data-testid="address-line1-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Address Line 2
                </Label>
                <Input
                  value={config.address_line2}
                  onChange={(e) => updateField('address_line2', e.target.value)}
                  placeholder="City, Country"
                  className="mt-1"
                  data-testid="address-line2-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium flex items-center gap-1">
                  <Phone className="h-3 w-3" /> Phone
                </Label>
                <Input
                  value={config.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+255 xxx xxx xxx"
                  className="mt-1"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium flex items-center gap-1">
                  <Mail className="h-3 w-3" /> Email
                </Label>
                <Input
                  value={config.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="info@company.com"
                  className="mt-1"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium flex items-center gap-1">
                <Globe className="h-3 w-3" /> Website
              </Label>
              <Input
                value={config.website}
                onChange={(e) => updateField('website', e.target.value)}
                placeholder="www.company.com"
                className="mt-1"
                data-testid="website-input"
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Custom Footer Text</Label>
              <Input
                value={config.footer_text}
                onChange={(e) => updateField('footer_text', e.target.value)}
                placeholder="Optional custom footer (leave blank for auto-generated)"
                className="mt-1"
                data-testid="footer-text-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">If blank, footer is auto-generated from your details above</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Preview Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Live Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 flex justify-center">
            <div className="w-full max-w-md aspect-[1/1.414] bg-white rounded shadow-lg overflow-hidden">
              {TEMPLATE_PREVIEWS[config.template]?.render(colors)}
            </div>
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            This is a schematic preview. Click "Preview PDF" for the full rendered document.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LetterheadSettings;
